import React, { useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { OutsideClickHandler } from 'magical-trails-react';

import Portal from './Portal';

import './styles/index.scss';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/index';

export type TModalTypes = 'standard' | 'fullScreen';
export interface Props {
  children: React.ReactNode;
  isOpen: boolean;
  handleClose: () => void;
  className?: string;
  type?: TModalTypes;
}

const Modal: React.FC<Props> = ({
  children,
  isOpen,
  handleClose,
  className,
  type = 'standard',
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // The element that had focus before the modal opened, so we can restore it.
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Close on Escape, and trap Tab focus within the dialog so keyboard users
  // can't wander onto the (inert) content behind the overlay.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const container = containerRef.current;
      if (!container) return;
      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusable.length === 0) {
        // Nothing focusable inside — keep focus pinned to the dialog itself.
        e.preventDefault();
        container.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === container) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.body.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.removeEventListener('keydown', onKeyDown);
    };
  }, [handleClose]);

  // Modal semantics: remember what had focus, move focus into the dialog when
  // it opens, and restore focus to the opener on close/unmount.
  useEffect(() => {
    if (!isOpen) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    containerRef.current?.focus();
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen]);

  const { visualViewport } = useSelector((state: RootState) => state.ui);

  if (!isOpen) return null;

  return (
    <Portal>
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Dialog"
        tabIndex={-1}
        className="Modal outline-none"
      >
        <div className="Modal_backdrop" />
        <OutsideClickHandler
          onOutsideClick={() => {
            if (type !== 'fullScreen') {
              handleClose();
            }
          }}
          className={`Modal_content${className ? ` ${className}` : ''}${
            type ? ` Modal_content_${type}` : ''
          }`}
        >
          {children}
          <button
            onClick={handleClose}
            type="button"
            className="Modal_closeButton bg-mt-surface hover:bg-mt-surface-2 focus-visible:outline-mt-primary z-50 rounded-full p-1.5 text-white shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            <XMarkIcon
              className="text-mt-ink-muted h-5 w-5"
              aria-hidden="true"
            />
            <span className="sr-only">Close</span>
          </button>
        </OutsideClickHandler>
      </div>
    </Portal>
  );
};

export default Modal;
